import {
  AccessibilitySchema,
  AudioLevelsSchema,
  BindingsSchema,
  DEFAULT_ACCESSIBILITY,
  DEFAULT_AUDIO,
  DEFAULT_BINDINGS,
  DEFAULT_GAMEPAD,
  GamepadSettingsSchema,
  type UserSettings,
} from '@steelwake/shared/progression';
import { z } from 'zod';

import {
  EFFECT_KEYS,
  PRESET_NAMES,
  PRESETS,
  SHADOW_QUALITIES,
  type EffectKey,
  type PresetName,
} from './presets.js';

export const SETTINGS_VERSION = 3;
/** localStorage key; bumped with SETTINGS_VERSION so old blobs fall back to defaults. */
export const SETTINGS_KEY = 'steelwake.settings.v3';

export { DEFAULT_ACCESSIBILITY, DEFAULT_AUDIO, PALETTES } from '@steelwake/shared/progression';
export type { Accessibility, AudioLevels, PaletteName } from '@steelwake/shared/progression';

const effectToggles = z
  .object(
    Object.fromEntries(EFFECT_KEYS.map((k) => [k, z.boolean()])) as Record<EffectKey, z.ZodBoolean>,
  )
  .strict();

export const SettingsSchema = z
  .object({
    version: z.literal(SETTINGS_VERSION),
    preset: z.enum(PRESET_NAMES),
    /** Maximum render scale; dynamic resolution scales below it, never above. */
    renderScale: z.number().min(0.5).max(1),
    dynamicResolution: z.boolean(),
    fpsCap: z.union([z.literal(0), z.literal(60), z.literal(120)]),
    shadows: z.enum(SHADOW_QUALITIES),
    effects: effectToggles,
    /** Skip WebGPU and use the WebGL 2 backend (takes effect on reload). */
    forceWebGL: z.boolean(),
    showStats: z.boolean(),
    fov: z.number().min(60).max(110),
    /** Mouse look multiplier (Phase 3); defaults keep Phase 2 saved settings valid. */
    mouseSensitivity: z.number().min(0.2).max(3).default(1),
    /** Floating damage numbers on hits. */
    damageNumbers: z.boolean().default(true),
    /** Ping, lead and correction readout during online matches (Phase 4). */
    showNetStats: z.boolean().default(false),
    /** The account subset (Phase 8); defaults keep earlier version 3 blobs valid. */
    bindings: BindingsSchema.default(DEFAULT_BINDINGS),
    gamepad: GamepadSettingsSchema.default(DEFAULT_GAMEPAD),
    audio: AudioLevelsSchema.default(DEFAULT_AUDIO),
    accessibility: AccessibilitySchema.default(DEFAULT_ACCESSIBILITY),
  })
  .strict();

export type Settings = z.infer<typeof SettingsSchema>;

export function presetSettings(name: PresetName): Settings {
  const p = PRESETS[name];
  return {
    version: SETTINGS_VERSION,
    preset: name,
    renderScale: p.renderScale,
    dynamicResolution: true,
    fpsCap: 60,
    shadows: p.shadows,
    effects: { ...p.effects },
    forceWebGL: false,
    showStats: true,
    fov: 75,
    mouseSensitivity: 1,
    damageNumbers: true,
    showNetStats: false,
    bindings: { ...DEFAULT_BINDINGS },
    gamepad: { ...DEFAULT_GAMEPAD },
    audio: { ...DEFAULT_AUDIO },
    accessibility: { ...DEFAULT_ACCESSIBILITY },
  };
}

export const DEFAULT_SETTINGS: Settings = presetSettings('high');

/** Applies a preset's quality fields while keeping the user's non-quality choices. */
export function applyPreset(settings: Settings, name: PresetName): Settings {
  const p = PRESETS[name];
  return {
    ...settings,
    preset: name,
    renderScale: p.renderScale,
    shadows: p.shadows,
    effects: { ...p.effects },
  };
}

/** The device-independent subset that follows the account (`user_settings`). */
export function userSettingsOf(settings: Settings): UserSettings {
  return {
    bindings: settings.bindings,
    gamepad: settings.gamepad,
    audio: settings.audio,
    accessibility: settings.accessibility,
  };
}

/** The local settings with the account's copy of the shared subset applied. */
export function withUserSettings(settings: Settings, user: UserSettings): Settings {
  return { ...settings, ...user };
}
